import React from "react";
import { cn } from "./utils";

interface TagProps {
  label: string;
  value?: string | number;
  title?: string;
  className?: string;
}

/**
 * Tag component for displaying key-value pairs or labels.
 * Commonly used for metadata tags.
 *
 * The key sits at lower emphasis than the value so a column of tags stays
 * scannable by key. Both clear WCAG AA against the chip's own `bg-muted`:
 * the previous `text-muted-foreground` treatment measured 4.31:1 in the light
 * theme, under the 4.5:1 floor for small text.
 *
 * The chip owns its text colours — `className` is for layout (padding, font
 * family, wrapping); a colour class passed there sits on the outer span and is
 * overridden by the inner ones.
 */
export function Tag({ label, value, title, className = "" }: TagProps) {
  const baseClasses = "px-2 py-1 rounded text-xs bg-muted";

  return (
    <span className={cn(baseClasses, className)} title={title}>
      {value !== undefined ? (
        <>
          <span className="text-foreground/65">{label}:</span>
          <span className="text-foreground font-medium">{value}</span>
        </>
      ) : (
        // A label-only chip has no second half to sit against, so it takes the
        // value's emphasis rather than the key's.
        <span className="text-foreground font-medium">{label}</span>
      )}
    </span>
  );
}

interface TagListProps {
  tags: Record<string, string | number> | Array<{ key: string; value: string | number }>;
  className?: string;
}

/**
 * TagList component for displaying multiple tags.
 * Accepts either an object or an array of tag objects.
 */
export function TagList({ tags, className = "" }: TagListProps) {
  const tagEntries = Array.isArray(tags)
    ? tags
    : Object.entries(tags).map(([key, value]) => ({ key, value }));

  if (tagEntries.length === 0) {
    return null;
  }

  return (
    <div className={`flex flex-wrap gap-1 ${className}`}>
      {tagEntries.map((tag) => (
        <Tag key={tag.key} label={tag.key} value={tag.value} />
      ))}
    </div>
  );
}
